import { History, Info } from "lucide-react";
import { useMemo, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { colocaSurfaceVariants } from "@/shared/components/layout/colocaSurfaceVariants";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { PaymentTax } from "../../types/paymentTaxes.types";
import { taxChanges, type TaxSource } from "../../utils/taxCoverage";
import { TaxChangeTimeline } from "./TaxChangeTimeline";

const PAGE_SIZE = 30;

export interface TaxChangesFeedProps {
  rows: PaymentTax[];
  now: number;
  isLoading: boolean;
  scopeOrganizationId: string | null;
  organizationNames: Record<string, string>;
  authorNames: Record<string, string>;
}

/** Every recorded change, newest first. */
export function TaxChangesFeed({
  rows,
  now,
  isLoading,
  scopeOrganizationId,
  organizationNames,
  authorNames,
}: TaxChangesFeedProps) {
  const { t } = useTranslations("paymentTaxes");
  const [source, setSource] = useState<TaxSource | "ALL">("ALL");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const feed = useMemo(() => taxChanges(rows, now), [rows, now]);
  const filtered = useMemo(
    () =>
      feed.filter((change) => {
        const owner = change.tax.organization_id;
        if (scopeOrganizationId && owner && owner !== scopeOrganizationId) return false;
        return source === "ALL" || change.source === source;
      }),
    [feed, scopeOrganizationId, source],
  );

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <ColocaSelect
          id="tax-feed-source"
          aria-label={t("feed_filter_source")}
          className="w-full sm:w-48"
          value={source}
          onValueChange={(value) => {
            setSource(value as TaxSource | "ALL");
            setVisible(PAGE_SIZE);
          }}
          options={[
            { value: "ALL", label: t("feed_source_all") },
            { value: "GENERAL", label: t("feed_source_general") },
            { value: "OWN", label: t("feed_source_own") },
          ]}
        />
        <span className="ml-auto text-xs text-muted-foreground">
          {t("feed_count", { count: filtered.length })}
        </span>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((index) => (
            <ColocaSkeleton key={index} className="h-20 rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <ColocaEmpty icon={History} title={t("feed_empty_title")} description={t("feed_empty_description")} />
      ) : (
        <div className={cn(colocaSurfaceVariants(), "px-5 py-3")}>
          <TaxChangeTimeline
            changes={filtered.slice(0, visible)}
            organizationNames={organizationNames}
            authorNames={authorNames}
            showScope
            groupByDay
          />
          {filtered.length > visible && (
            <div className="pt-2 pb-1">
              <ColocaButton
                variant="default"
                size="sm"
                onClick={() => setVisible((count) => count + PAGE_SIZE)}
              >
                {t("feed_load_more")}
              </ColocaButton>
            </div>
          )}
        </div>
      )}

      <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        {t("history_deleted_note")}
      </p>
    </div>
  );
}
